import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useParallaxRef } from '@/utils/parallax';
import { useRevealOnce } from '@/hooks/useRevealOnce';
import { Card, CardContent } from '@/components/ui/card';
import Marquee from '@/components/lib/magicui/Marquee';
import { useGradientBackground } from '@/utils/gradientBackground';

interface Testimonial {
  name: string;
  role: string;
  business: string;
  quote: string;
  result: string;
}

const testimonials: Testimonial[] = [];

const microQuotes: string[] = [];

export default function ServicesTestimonialsSection() {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      setPrefersReducedMotion(mediaQuery.matches);

      const handleChange = (e: MediaQueryListEvent) => {
        setPrefersReducedMotion(e.matches);
      };

      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, []);

  // Parallax for subtitle
  const subtitleRef = useParallaxRef({
    speed: -0.12,
    axis: 'y',
    maxTranslate: 10,
  });

  // Parallax for H2 (subtle)
  const titleRef = useParallaxRef({
    speed: -0.06,
    axis: 'y',
    maxTranslate: 6,
  });

  // Reveal hook for section entrance
  const sectionReveal = useRevealOnce({ amount: 0.2 });

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.1,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' as const },
    },
  };

      const backgroundStyle = useGradientBackground();

      // No testimonials or quotes to show: the section (heading included) does not render
      if (testimonials.length === 0 && microQuotes.length === 0) return null;

      return (
        <section className="relative w-full text-slate-900 py-16 md:py-24" style={backgroundStyle}>
      <div className="container mx-auto px-4">
        <div className="max-w-7xl mx-auto">
          <motion.div
            ref={sectionReveal.ref}
            variants={containerVariants}
            initial="hidden"
            animate={sectionReveal.controls}
          >
            {/* Heading Section */}
            <div className="text-center mb-12 md:mb-16">
              <h2
                ref={titleRef as any}
                className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground mb-6"
              >
                What clients say after launch
              </h2>
              <p
                ref={subtitleRef as any}
                className="text-lg md:text-xl text-slate-600 leading-relaxed max-w-3xl mx-auto"
              >
                Most people come in feeling overwhelmed by their domain, their offers, or their site. The goal is that you leave with a clear brand, a working website, and more confidence talking to the right clients.
              </p>
            </div>

            {/* Testimonial Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 mb-12 md:mb-16">
              {testimonials.map((testimonial, index) => (
                <motion.div key={index} variants={cardVariants}>
                  <Card className="h-full bg-white border-slate-200 rounded-xl shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md">
                    <CardContent className="p-6">
                      {/* Quote */}
                      <blockquote className="text-slate-800 leading-relaxed mb-6">
                        "{testimonial.quote}"
                      </blockquote>

                      {/* Author Info */}
                      <div className="flex items-start gap-3 mb-4">
                        {/* Initial Circle */}
                        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                          <span className="text-blue-700 font-semibold text-sm">
                            {testimonial.name.charAt(0)}
                          </span>
                        </div>
                        <div>
                          <div className="font-semibold text-slate-900">{testimonial.name}</div>
                          <div className="text-sm text-slate-500">
                            {testimonial.role}, {testimonial.business}
                          </div>
                        </div>
                      </div>

                      {/* Result Line */}
                      <div className="pt-4 border-t border-slate-200">
                        <p className="text-sm font-semibold text-blue-700">
                          <span className="font-bold">Result:</span> {testimonial.result}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>

            {/* Marquee Strip */}
            <div className="bg-slate-50 rounded-lg py-6 mb-8">
              {prefersReducedMotion ? (
                <div className="flex flex-wrap justify-center gap-6 px-4">
                  {microQuotes.map((quote, index) => (
                    <p key={index} className="text-sm text-slate-600 italic">
                      "{quote}"
                    </p>
                  ))}
                </div>
              ) : (
                <Marquee pauseOnHover className="[--duration:50s]">
                  {microQuotes.map((quote, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-center px-6 text-sm text-slate-600 italic whitespace-nowrap"
                    >
                      "{quote}"
                    </div>
                  ))}
                </Marquee>
              )}
            </div>

            {/* Optional Small Link */}
            <div className="text-center">
              <Link
                to="/services/consult"
                className="text-blue-600 font-medium hover:underline transition-colors duration-200"
              >
                Want to know whether your situation is similar? Book a free 1-hour consult →
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

